import React, { useRef, useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import { STATUS } from "./harvestEntryConstants.js";
import { isValidBackendDate } from "./harvestEntryHelpers.js";

export const StatusEditCell = React.memo(function StatusEditCell({
  onClose,
  apply,
  current,
  getPopupContainer,
  dropdownZIndex,
}) {
  const [open, setOpen] = useState(true);

  return (
    <div
      style={{ width: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Select
        value={current}
        style={{ width: "100%" }}
        className="harvest-cell-editor"
        variant="borderless"
        open={open}
        autoFocus
        getPopupContainer={getPopupContainer}
        styles={{ popup: { root: { zIndex: dropdownZIndex } } }}
        options={[
          { value: STATUS.PLANTED, label: "Planted" },
          { value: STATUS.HARVESTED, label: "Harvested" },
          { value: STATUS.DISCARDED, label: "Discarded" },
        ]}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) requestAnimationFrame(() => onClose(true));
        }}
        onChange={(next) => {
          apply(next);
          setOpen(false);
        }}
      />
    </div>
  );
});

export const HarvestDateEditCell = React.memo(function HarvestDateEditCell({
  row,
  onRowChange,
  onClose,
  rowIdx,
  column,
  move,
  setDefaultHarvestDateISO,
  getPopupContainer,
  dropdownZIndex,
}) {
  const [open, setOpen] = useState(true);

  const raw = row.pendingHarvestDate || row.harvest_date || null;
  const value =
    raw && isValidBackendDate(raw) ? dayjs(raw, BACKEND_DATE_FORMAT) : null;

  return (
    <div
      style={{ width: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        const k = e.key;
        if (
          k === "Enter" ||
          k === "ArrowDown" ||
          k === "ArrowUp" ||
          k === "ArrowLeft" ||
          k === "ArrowRight"
        ) {
          e.preventDefault();
          onClose(true);
          setTimeout(() => move({ rowIdx, idx: column.idx }, k, column.idx), 0);
        }
      }}
    >
      <DatePicker
        value={value}
        className="harvest-cell-editor"
        variant="borderless"
        allowClear
        format="DD MMM, YYYY"
        getPopupContainer={getPopupContainer}
        styles={{ popup: { root: { zIndex: dropdownZIndex } } }}
        style={{ width: "100%" }}
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) requestAnimationFrame(() => onClose(true));
        }}
        onChange={(d) => {
          const next = d ? d.format(BACKEND_DATE_FORMAT) : null;
          const saved = row.harvest_date ?? null;

          if (next && isValidBackendDate(next)) setDefaultHarvestDateISO(next);

          const base = { ...row, pendingUnharvest: false };

          if ((next ?? null) === (saved ?? null)) {
            onRowChange({ ...base, pendingHarvestDate: null }, true);
          } else {
            onRowChange({ ...base, pendingHarvestDate: next }, true);
          }

          setOpen(false);
        }}
        inputReadOnly
        autoFocus
      />
    </div>
  );
});

export const TubWeightEditCell = React.memo(function TubWeightEditCell({
  row,
  onRowChange,
  onClose,
  ensurePendingDate,
  harvestConfig,
  onTubWeightChange,
  move,
  rowIdx,
  column,
}) {
  const savedValue = row.weight_per_tub ?? (harvestConfig?.tub_weight?.default ?? 0);
  const currentValue =
    row.pendingTubWeight !== null && row.pendingTubWeight !== undefined
      ? row.pendingTubWeight
      : savedValue;
  const [localVal, setLocalVal] = useState(
    currentValue === null || currentValue === undefined ? "" : String(currentValue),
  );

  return (
    <input
      inputMode="decimal"
      value={localVal}
      autoFocus
      style={{
        width: "100%",
        height: 34,
        padding: "0 8px",
        textAlign: "center",
        boxSizing: "border-box",
        outline: "none",
        border: "0",
        boxShadow: "none",
        background: "transparent",
      }}
      onFocus={(e) => e.target.select()}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
        setLocalVal(raw);
        const n = raw === "" ? null : parseFloat(raw);
        const next = Number.isFinite(n) ? n : null;
        const newVal = next === null || next === savedValue ? null : next;
        const base = ensurePendingDate({ ...row, pendingUnharvest: false });
        onRowChange({ ...base, pendingTubWeight: newVal });
        onTubWeightChange?.(newVal);
      }}
      onBlur={() => onClose(true)}
      onKeyDown={(e) => {
        const k = e.key;
        if (k === "Escape") {
          e.stopPropagation();
          onClose(false);
          return;
        }
        if (k === "Enter" || k === "ArrowDown" || k === "ArrowUp") {
          e.preventDefault();
          onClose(true);
          setTimeout(() => move({ rowIdx, idx: column.idx }, k, column.idx), 0);
        }
      }}
    />
  );
});

export const DiscardReasonEditCell = React.memo(function DiscardReasonEditCell({
  row,
  onRowChange,
  onClose,
  harvestConfig,
  getPopupContainer,
  dropdownZIndex,
  ensurePendingDate,
  showWeight,
}) {
  const [open, setOpen] = useState(true);
  // Ref (not state) so the synchronous onOpenChange handler always sees the
  // latest value without relying on React's batched state flush.
  const selectedRef = useRef(false);

  const backendIds = (row.ploughed_under_reasons ?? []).map((r) => r.id);
  const currentIds =
    row.pendingPloughedReasons !== null && row.pendingPloughedReasons !== undefined
      ? row.pendingPloughedReasons
      : backendIds;

  const options = [...(harvestConfig?.discard_reasons ?? [])]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((r) => ({ value: r.id, label: r.label }));

  // True when this cell is open on a row not yet marked as discarded.
  // The editable() guard ensures we only reach here for planted or discarded
  // rows, so this cleanly separates the two cases.
  const effectiveYield =
    row.pendingYield !== null && row.pendingYield !== undefined
      ? row.pendingYield
      : row.yield;
  const isPlantedRow = effectiveYield !== -1 && !row.pendingUnharvest;

  const applyReason = (nextId) => {
    const nextIds = nextId != null ? [nextId] : [];
    const sorted = (arr) => [...arr].sort().join(",");
    const isSameAsBackend = sorted(nextIds) === sorted(backendIds);

    let nextRow = {
      ...row,
      pendingPloughedReasons: isSameAsBackend ? null : nextIds,
    };

    // Auto-transition planted rows to Discarded when a reason is applied.
    if (isPlantedRow && nextIds.length > 0) {
      const base = ensurePendingDate({ ...nextRow, pendingUnharvest: false });
      nextRow = {
        ...base,
        pendingYield: -1,
        ...(showWeight ? { pendingWeight: -1 } : {}),
      };
    }

    onRowChange(nextRow, true);
  };

  return (
    <div
      style={{ width: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Select
        value={currentIds[0] ?? undefined}
        style={{ width: "100%" }}
        className="harvest-cell-editor"
        variant="borderless"
        open={open}
        autoFocus
        getPopupContainer={getPopupContainer}
        styles={{ popup: { root: { zIndex: dropdownZIndex } } }}
        options={options}
        allowClear
        placeholder="Select reason…"
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            // Apply first reason when dismissed without selecting if:
            // - planted row (will auto-transition to discarded), OR
            // - already discarded but has no reason yet (e.g. note was typed first)
            const isDiscardedWithNoReason = effectiveYield === -1 && currentIds.length === 0;
            if (!selectedRef.current && (isPlantedRow || isDiscardedWithNoReason) && options.length > 0) {
              applyReason(options[0].value);
            }
            requestAnimationFrame(() => onClose(true));
          }
        }}
        onChange={(nextId) => {
          selectedRef.current = true;
          applyReason(nextId);
          setOpen(false);
        }}
      />
    </div>
  );
});

export const DiscardNoteEditCell = React.memo(function DiscardNoteEditCell({
  row,
  onRowChange,
  onClose,
  onNoteChange,
  buildNextRow,
  move,
  rowIdx,
  column,
}) {
  const savedNote = row.ploughed_under_note ?? "";
  // Local state so the input is never controlled by parent rows — prevents
  // focus loss if the parent re-renders while the editor is open.
  const [localVal, setLocalVal] = useState(row.pendingPloughedNote ?? savedNote);

  return (
    <div
      style={{ width: "100%", height: "100%" }}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        value={localVal}
        placeholder="Add note…"
        autoFocus
        style={{
          width: "100%",
          height: "100%",
          padding: "0 8px",
          boxSizing: "border-box",
          border: "none",
          boxShadow: "none",
          background: "transparent",
        }}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const next = e.target.value;
          setLocalVal(next);
          const newPending = next === savedNote ? null : next;
          onNoteChange?.(newPending);
          const nextRow = buildNextRow
            ? buildNextRow(newPending)
            : { ...row, pendingPloughedNote: newPending };
          onRowChange(nextRow);
        }}
        onBlur={() => onClose(true)}
        onKeyDown={(e) => {
          e.stopPropagation();
          const k = e.key;
          if (k === "Escape") {
            onClose(false);
            return;
          }
          if (k === "Enter" || k === "ArrowDown" || k === "ArrowUp") {
            e.preventDefault();
            onClose(true);
            setTimeout(() => move?.({ rowIdx, idx: column.idx }, k, column.idx), 0);
          }
        }}
      />
    </div>
  );
});
